import React, { useState, useCallback } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Send, Mail, Phone, MapPin, Shield } from 'lucide-react';

const ContactForm = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!executeRecaptcha) {
      toast.error('reCAPTCHA nicht geladen. Bitte Seite neu laden.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Get reCAPTCHA v3 token
      const captchaToken = await executeRecaptcha('contact_form');
      
      // PHP Backend URL - für Lima-City wird dies /api/contact.php sein
      const response = await fetch('/api/contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          captchaToken: captchaToken
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Nachricht gesendet!', {
          description: data.message || 'Vielen Dank für Ihre Anfrage. Wir melden uns in Kürze bei Ihnen.'
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: ''
        });
      } else {
        toast.error('Fehler beim Senden', {
          description: data.error || 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.'
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Verbindungsfehler', {
        description: 'Die Nachricht konnte nicht gesendet werden. Bitte prüfen Sie Ihre Internetverbindung.'
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [executeRecaptcha, formData]);

  return (
    <div className="grid lg:grid-cols-3 gap-8">
      {/* Contact Info */}
      <div className="space-y-6">
        <Card className="group hover:shadow-lg transition-all hover:border-orange-200">
          <CardHeader>
            <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-red-100 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <Mail className="text-orange-500" size={24} />
            </div>
            <CardTitle className="text-lg">E-Mail</CardTitle>
            <CardDescription>
              <a href="mailto:info@apppilot.de" className="text-orange-500 hover:text-orange-600 font-medium">
                info@apppilot.de
              </a>
            </CardDescription>
          </CardHeader>
        </Card>

        <Card className="group hover:shadow-lg transition-all hover:border-orange-200">
          <CardHeader>
            <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-red-100 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <Phone className="text-orange-500" size={24} />
            </div>
            <CardTitle className="text-lg">Telefon</CardTitle>
            <CardDescription>
              <a href="tel:+4915223958867" className="text-orange-500 hover:text-orange-600 font-medium">
                +49 (0) 152 239 588 67
              </a>
            </CardDescription>
          </CardHeader>
        </Card>

        <Card className="group hover:shadow-lg transition-all hover:border-orange-200">
          <CardHeader>
            <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-red-100 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <MapPin className="text-orange-500" size={24} />
            </div>
            <CardTitle className="text-lg">Standort</CardTitle>
            <CardDescription className="text-gray-600">
              Deutschland
              <br />
              <span className="text-sm">Server in EU/DE</span>
            </CardDescription>
          </CardHeader>
        </Card>
      </div>

      {/* Contact Form */}
      <Card className="lg:col-span-2 shadow-xl hover:shadow-2xl transition-shadow">
        <CardHeader>
          <CardTitle className="text-2xl">Senden Sie uns eine Nachricht</CardTitle>
          <CardDescription className="text-base">
            Füllen Sie das Formular aus und wir melden uns schnellstmöglich bei Ihnen.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-gray-700 font-medium">
                  Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Max Mustermann"
                  required
                  className="border-gray-300 focus:border-orange-500 focus:ring-orange-500 transition-colors"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700 font-medium">
                  E-Mail *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="max@unternehmen.de"
                  required
                  className="border-gray-300 focus:border-orange-500 focus:ring-orange-500 transition-colors"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-gray-700 font-medium">
                  Telefon
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+49 123 456789"
                  className="border-gray-300 focus:border-orange-500 focus:ring-orange-500 transition-colors"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company" className="text-gray-700 font-medium">
                  Unternehmen
                </Label>
                <Input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Ihr Unternehmen"
                  className="border-gray-300 focus:border-orange-500 focus:ring-orange-500 transition-colors"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message" className="text-gray-700 font-medium">
                Ihre Nachricht *
              </Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Beschreiben Sie Ihr Projekt oder Ihre Anfrage..."
                required
                rows={6}
                className="border-gray-300 focus:border-orange-500 focus:ring-orange-500 transition-colors resize-none"
              />
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  size="lg"
                >
                  {isSubmitting ? (
                    <>
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                      Wird gesendet...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Nachricht senden
                    </>
                  )}
                </Button>
                <p className="text-sm text-gray-500">* Pflichtfelder</p>
              </div>
              
              {/* reCAPTCHA v3 Badge Info */}
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <Shield size={14} />
                <span>
                  Diese Website wird durch reCAPTCHA geschützt. Es gelten die{' '}
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">
                    Datenschutzbestimmungen
                  </a>{' '}
                  und{' '}
                  <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">
                    Nutzungsbedingungen
                  </a>{' '}
                  von Google.
                </span>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContactForm;
